const EIGHTH = 0.125;
const roundEighth = (value) => Math.round(Number(value) / EIGHTH) * EIGHTH;

const truthy = (value) => !(value === false || value === 0 || value === '0');

export function isLadiesShirtBody(measurements = {}) {
  return String(measurements.shirtBodyStyle || 'gents').toLowerCase() === 'ladies';
}

export function resolveFrontFishDartIntake(measurements = {}) {
  if (!isLadiesShirtBody(measurements)) {
    return { waist: 0, belly: 0 };
  }
  if (!truthy(measurements.frontFishDartEnabled)) {
    return { waist: 0, belly: 0 };
  }
  return {
    waist: roundEighth(Number(measurements.frontDartWidthWaist ?? measurements.dartWidthWaist ?? 1) || 0),
    belly: roundEighth(Number(measurements.frontDartWidthBelly ?? measurements.dartWidthBelly ?? 0.5) || 0),
  };
}

function segmentLength(p0, p1) {
  return Math.hypot(p1.x - p0.x, p1.y - p0.y);
}

function shiftPointY(point, deltaY) {
  if (!point) return point;
  point.y = roundEighth(point.y + deltaY);
  return point;
}

function quadPoint(p0, p1, p2, t) {
  const u = 1 - t;
  return {
    x: u * u * p0.x + 2 * u * t * p1.x + t * t * p2.x,
    y: u * u * p0.y + 2 * u * t * p1.y + t * t * p2.y,
  };
}

function buildQuadArcTable(p0, p1, p2, steps = 64) {
  const table = [{ t: 0, point: { ...p0 }, length: 0 }];
  let total = 0;
  for (let i = 1; i <= steps; i += 1) {
    const t = i / steps;
    const point = quadPoint(p0, p1, p2, t);
    total += segmentLength(table[table.length - 1].point, point);
    table.push({ t, point, length: total });
  }
  return { table, totalLength: total };
}

function pointAtArcLength(p0, p1, p2, targetLength) {
  const safeTarget = Math.max(0, targetLength);
  const { table, totalLength } = buildQuadArcTable(p0, p1, p2);
  if (safeTarget >= totalLength) {
    const end = table[table.length - 1].point;
    return { x: roundEighth(end.x), y: roundEighth(end.y) };
  }
  for (let i = 1; i < table.length; i += 1) {
    const prev = table[i - 1];
    const next = table[i];
    if (next.length >= safeTarget) {
      const span = next.length - prev.length || 1;
      const ratio = (safeTarget - prev.length) / span;
      const point = quadPoint(p0, p1, p2, prev.t + (next.t - prev.t) * ratio);
      return { x: roundEighth(point.x), y: roundEighth(point.y) };
    }
  }
  const end = table[table.length - 1].point;
  return { x: roundEighth(end.x), y: roundEighth(end.y) };
}

function lerpPoint(a, b, t) {
  return {
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
  };
}

function splitQuadAt(p0, p1, p2, t) {
  const q0 = lerpPoint(p0, p1, t);
  const q1 = lerpPoint(p1, p2, t);
  const r = lerpPoint(q0, q1, t);
  return {
    before: { p0, p1: q0, p2: r },
    after: { p0: r, p1: q1, p2 },
  };
}

function findQuadParameterForPoint(p0, p1, p2, target) {
  const { table } = buildQuadArcTable(p0, p1, p2);
  let best = table[0];
  let bestDist = Infinity;
  table.forEach((entry) => {
    const d = segmentLength(entry.point, target);
    if (d < bestDist) {
      bestDist = d;
      best = entry;
    }
  });
  return best.t;
}

function measureNetFrontChestToWaist(FCP, ctrl, FWP, BDA1, BDA2) {
  const t1 = findQuadParameterForPoint(FCP, ctrl, FWP, BDA1);
  const t2 = findQuadParameterForPoint(FCP, ctrl, FWP, BDA2);
  const { table, totalLength } = buildQuadArcTable(FCP, ctrl, FWP);
  const lengthTo = (t) => {
    const entry = table.reduce((acc, row) => (Math.abs(row.t - t) < Math.abs(acc.t - t) ? row : acc), table[0]);
    return entry.length;
  };
  const upper = lengthTo(t1);
  const lower = totalLength - lengthTo(t2);
  return roundEighth(upper + lower);
}

function buildBustChestWaistSideCommand(FCP, ctrl, FWP, BDA1, BDA2, BDA3, toSVG) {
  const t1 = findQuadParameterForPoint(FCP, ctrl, FWP, BDA1);
  const t2 = Math.max(t1 + 1e-4, findQuadParameterForPoint(FCP, ctrl, FWP, BDA2));
  const upperSplit = splitQuadAt(FCP, ctrl, FWP, t1);
  const lowerSplit = splitQuadAt(FCP, ctrl, FWP, t2);
  return `
    Q ${toSVG(upperSplit.before.p1)} ${toSVG(BDA1)}
    L ${toSVG(BDA3)}
    L ${toSVG(BDA2)}
    Q ${toSVG(lowerSplit.after.p1)} ${toSVG(FWP)}
  `.trim().replace(/\s+/g, ' ');
}

/**
 * Bust dart on front side seam: FCP→BDA1→BDA3→BDA2→FWP with dart legs BDA1–BDA3–BDA2.
 * BDA1–BDA2 arc on the chest→waist curve is the intake; lower front block drops to match back side-seam segments.
 */
export function applyLadiesBustDartAndExtension({
  measurements,
  rp,
  toSVG,
  xCF,
  FCP,
  FWP,
  FBP,
  FSP,
  FHP,
  CF4,
  CF5,
  CF6,
  CF7,
  E7,
  PL7,
  frontChestWaistCtrl,
  frontWaistBellyCtrl,
  frontBellySeatCtrl,
  backSideChestToWaist,
  backSideWaistToBelly,
  backSideBellyToSeat,
}) {
  const inactive = {
    bustDartPath: '',
    bustConstructionLines: [],
    bustDartPoints: {},
    bustDartIntake: 0,
    frontSideChestToWaist: segmentLength(FCP, FWP),
    frontSideWaistToBelly: segmentLength(FWP, FBP),
    frontSideBellyToSeat: segmentLength(FBP, FSP),
    sideSeamExtensionApplied: 0,
  };

  if (!isLadiesShirtBody(measurements) || !truthy(measurements.bustDartEnabled)) {
    return inactive;
  }

  const leadFromChest = Math.max(0, Number(measurements.bustDartLeadFromChest ?? 2) || 0);
  const openingLength = Math.max(EIGHTH, Number(
    measurements.bustDartOpeningLength
    ?? measurements.bustDartWidth
    ?? 1,
  ) || 0);
  const apexFromSide = Math.max(0, Number(measurements.bustDartApexFromSide ?? 4) || 0);

  const lowerPoints = [FWP, FBP, FSP, FHP, CF4, CF5, CF6, CF7, E7, PL7].filter(Boolean);
  const shiftLower = (deltaY) => {
    lowerPoints.forEach((point) => shiftPointY(point, deltaY));
    shiftPointY(frontChestWaistCtrl, deltaY * 0.35);
    shiftPointY(frontWaistBellyCtrl, deltaY * 0.65);
    shiftPointY(frontBellySeatCtrl, deltaY * 0.85);
  };

  const chestFull = Number(measurements.chest) || 0;
  const bustFull = Number(measurements.bust ?? chestFull) || chestFull;
  const bustSpread = roundEighth(Math.max(0, (bustFull - chestFull) / 2));

  const computeDartPoints = () => {
    const openStart = pointAtArcLength(FCP, frontChestWaistCtrl, FWP, leadFromChest);
    const openEnd = pointAtArcLength(FCP, frontChestWaistCtrl, FWP, leadFromChest + openingLength);
    const outwardSign = openStart.x <= xCF ? -1 : 1;
    const BDA1 = rp(
      roundEighth(openStart.x + outwardSign * bustSpread),
      openStart.y,
    );
    const BDA2 = rp(
      roundEighth(openEnd.x + outwardSign * bustSpread),
      openEnd.y,
    );
    const midX = (BDA1.x + BDA2.x) / 2;
    const midY = (BDA1.y + BDA2.y) / 2;
    const towardCfSign = xCF >= BDA1.x ? 1 : -1;
    const BDA3 = rp(
      roundEighth(midX + towardCfSign * apexFromSide),
      roundEighth(midY),
    );
    const bustDartIntake = roundEighth(segmentLength(openStart, openEnd));
    return { BDA1, BDA2, BDA3, bustDartIntake, bustSpread };
  };

  shiftLower(openingLength);
  let sideSeamExtensionApplied = openingLength;
  let BDA1;
  let BDA2;
  let BDA3;
  let bustDartIntake = openingLength;
  let bustSpreadApplied = bustSpread;

  for (let i = 0; i < 32; i += 1) {
    ({ BDA1, BDA2, BDA3, bustDartIntake, bustSpread: bustSpreadApplied } = computeDartPoints());
    const chestWaist = measureNetFrontChestToWaist(FCP, frontChestWaistCtrl, FWP, BDA1, BDA2);
    const waistBelly = segmentLength(FWP, FBP);
    const bellySeat = segmentLength(FBP, FSP);
    const deltaChestWaist = backSideChestToWaist - chestWaist;
    const deltaWaistBelly = backSideWaistToBelly - waistBelly;
    const deltaBellySeat = backSideBellyToSeat - bellySeat;
    const maxDelta = Math.max(Math.abs(deltaChestWaist), Math.abs(deltaWaistBelly), Math.abs(deltaBellySeat));
    if (maxDelta <= EIGHTH / 4) break;
    const step = roundEighth(Math.max(-0.5, Math.min(0.5, (deltaChestWaist + deltaWaistBelly + deltaBellySeat) / 3)));
    if (Math.abs(step) < EIGHTH / 4) break;
    shiftLower(step);
    sideSeamExtensionApplied = roundEighth(sideSeamExtensionApplied + step);
  }

  ({ BDA1, BDA2, BDA3, bustDartIntake, bustSpread: bustSpreadApplied } = computeDartPoints());

  const bustDartPoints = { BDA1, BDA2, BDA3 };
  const bustDartPath = `
    M ${toSVG(BDA1)} L ${toSVG(BDA3)} L ${toSVG(BDA2)}
  `;

  return {
    bustDartPath,
    BDA1,
    BDA2,
    BDA3,
    bustDartPoints,
    bustDartIntake,
    bustSpreadApplied,
    bustConstructionLines: [
      {
        p1: rp(xCF, BDA3.y),
        p2: rp(BDA1.x, BDA3.y),
        label: 'Bust line',
      },
    ],
    frontSideChestToWaist: measureNetFrontChestToWaist(FCP, frontChestWaistCtrl, FWP, BDA1, BDA2),
    frontChestToWaistSideCommand: buildBustChestWaistSideCommand(
      FCP,
      frontChestWaistCtrl,
      FWP,
      BDA1,
      BDA2,
      BDA3,
      toSVG,
    ),
    frontSideWaistToBelly: segmentLength(FWP, FBP),
    frontSideBellyToSeat: segmentLength(FBP, FSP),
    sideSeamExtensionApplied,
  };
}

export function buildFrontFishDartGeometry({
  measurements,
  rp,
  toSVG,
  xCF,
  CF4,
  CF5,
  FWP,
  FBP,
}) {
  if (!isLadiesShirtBody(measurements) || !truthy(measurements.frontFishDartEnabled)) {
    return { frontFishDartPath: '', frontFishDartLines: [] };
  }

  const dartWidthWaist = roundEighth(Number(measurements.frontDartWidthWaist ?? measurements.dartWidthWaist ?? 1) || 0);
  const dartWidthBelly = roundEighth(Number(measurements.frontDartWidthBelly ?? measurements.dartWidthBelly ?? 0.5) || 0);
  const dartLengthTop = roundEighth(Number(measurements.frontDartLengthTop ?? measurements.dartLengthTop ?? 5) || 0);
  const dartLengthBottom = roundEighth(Number(measurements.frontDartLengthBottom ?? measurements.dartLengthBottom ?? 4) || 0);

  const dartCenterX = roundEighth((CF4.x + FWP.x) / 2);
  const FD1 = rp(dartCenterX - dartWidthWaist / 2, CF4.y);
  const FD2 = rp(dartCenterX + dartWidthWaist / 2, CF4.y);
  const FD3 = rp(dartCenterX - dartWidthBelly / 2, CF5.y);
  const FD4 = rp(dartCenterX + dartWidthBelly / 2, CF5.y);
  const FD0 = rp(dartCenterX, CF4.y - dartLengthTop);
  const FD5 = rp(dartCenterX, CF5.y + dartLengthBottom);

  const frontFishDartPath = `
    M ${toSVG(FD1)} L ${toSVG(FD0)} L ${toSVG(FD2)}
    M ${toSVG(FD3)} L ${toSVG(FD5)} L ${toSVG(FD4)}
    M ${toSVG(FD1)} L ${toSVG(FD3)}
    M ${toSVG(FD2)} L ${toSVG(FD4)}
  `;

  return {
    frontFishDartPath,
    frontFishDartLines: [
      { id: 'front-waist-dart-left', p1: FD2, p2: FD3 },
      { id: 'front-waist-dart-right', p1: FD1, p2: FD4 },
    ],
    frontFishDartPoints: { FD0, FD1, FD2, FD3, FD4, FD5 },
  };
}
